import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Mail, Send, Megaphone, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useSettings } from "@/context/SettingsContext";

export default function PromoTab() {
  const { settings } = useSettings();
  const [subs, setSubs] = useState([]);
  const [subject, setSubject] = useState("Enjoy 10% off your next caricature! 🎨");
  const [message, setMessage] = useState("Hey there! As a thank you for being on our list, here's 10% off your next custom caricature. Perfect for birthdays, gifts, or just for fun. Can't wait to draw you!");
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/admin/promo-subscribers").then((r) => setSubs(r.data)).catch(() => {}); }, []);

  const copyAll = () => {
    navigator.clipboard.writeText(subs.map((s) => s.email).join(", "));
    toast.success("Emails copied to clipboard!");
  };

  const sendBlast = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Add a subject and message"); return; }
    if (subs.length === 0) { toast.error("No subscribers to send to yet"); return; }
    setSending(true);
    try {
      const res = await api.post("/admin/promo-blast", { subject, message });
      if (res.data.email_enabled) {
        toast.success(`Sent to ${res.data.sent}/${res.data.recipients} subscribers! 🚀`);
      } else {
        toast.info(`Prepared for ${res.data.recipients} subscribers. Connect Resend to actually send.`);
      }
    } catch { toast.error("Failed to send blast"); }
    finally { setSending(false); }
  };

  const emailEnabled = settings?.email_enabled;

  return (
    <div data-testid="admin-promo">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl mb-1">Email List</h1>
          <p className="text-ink/60">{subs.length} people opted in for 10% off & promos.</p>
        </div>
        {subs.length > 0 && <button onClick={copyAll} className="brut-btn bg-sun" data-testid="copy-emails"><Copy className="w-4 h-4" /> Copy all emails</button>}
      </div>

      {/* PROMO BLAST */}
      <div className="brut-card p-6 mb-6 bg-bubble/10" data-testid="promo-blast-card">
        <h2 className="font-display font-bold text-2xl flex items-center gap-2 mb-1"><Megaphone className="w-6 h-6 text-bubble" /> Send a 10% off blast</h2>
        <p className="text-ink/60 mb-4">Email everyone on your list with a promo in one click.</p>

        {!emailEnabled && (
          <div className="brut border-ink bg-yellow-100 p-3 mb-4 flex items-start gap-2 text-sm font-semibold" data-testid="email-disabled-warning">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <span>Real email sending is off (Resend not connected). Add a Resend API key and I'll enable live delivery — for now blasts are prepared & logged.</span>
          </div>
        )}

        <label className="font-display font-semibold text-sm">Subject</label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="brut-input mt-1 mb-4" data-testid="blast-subject" />
        <label className="font-display font-semibold text-sm">Message</label>
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="brut-input mt-1 mb-4 min-h-32" data-testid="blast-message" />
        <button onClick={sendBlast} disabled={sending} className="brut-btn bg-bubble text-white text-lg" data-testid="send-blast">
          <Send className="w-5 h-5" /> {sending ? "Sending…" : `Send blast to ${subs.length}`}
        </button>
      </div>

      {subs.length === 0 ? (
        <div className="brut-card p-6 text-ink/60">No subscribers yet.</div>
      ) : (
        <div className="brut-card overflow-hidden">
          {subs.map((s, i) => (
            <div key={s.email} className={`flex items-center gap-3 px-5 py-3 ${i % 2 ? "bg-cream-solid" : "bg-white"}`} data-testid={`promo-sub-${i}`}>
              <Mail className="w-4 h-4 text-bubble" />
              <span className="font-semibold">{s.email}</span>
              {s.name && <span className="text-ink/50 text-sm">· {s.name}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
